import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/Button';
import Input from '@/components/Input';

interface EditCategoryModalProps {
  isOpen: boolean;
  categoryId: number | null;
  initialName: string;
  onClose: () => void;
  onSubmit: (id: number, name: string) => void;
  isLoading?: boolean;
  isReordering?: boolean;
}

/**
 * カスタムカテゴリ編集モーダル
 */
export default function EditCategoryModal({
  isOpen,
  categoryId,
  initialName,
  onClose,
  onSubmit,
  isLoading = false,
  isReordering = false,
}: EditCategoryModalProps) {
  const [categoryName, setCategoryName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // モーダルが開いたら初期値を設定
  useEffect(() => {
    if (isOpen) {
      setCategoryName(initialName);
      setError(null);
    }
  }, [isOpen, initialName]);

  const handleSubmit = () => {
    if (!categoryId) return;

    // バリデーション
    if (!categoryName.trim()) {
      setError('区分名を入力してください');
      return;
    }
    if (categoryName.length < 2 || categoryName.length > 15) {
      setError('区分名は2文字以上15文字以下で入力してください');
      return;
    }

    setError(null);
    onSubmit(categoryId, categoryName);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="w-[640px]">
        <AlertDialogHeader>
          <AlertDialogTitle>区分を編集</AlertDialogTitle>
        </AlertDialogHeader>

        {/* コンテンツ */}
        <div className="px-6 py-4">
          <div className="flex gap-6">
            {/* タイトル入力フィールド */}
            <div className="flex w-[236px] flex-col gap-2">
              <div className="flex items-center gap-2">
                <label className="text-sm font-bold text-gray-900">区分名</label>
                <span className="h-5 rounded-full bg-red-50 px-3 text-xs leading-5 font-bold text-red-600">
                  必須
                </span>
              </div>
              <Input
                type="text"
                value={categoryName}
                onChange={(e) => {
                  setCategoryName(e.target.value);
                  setError(null);
                }}
                placeholder="例:プロフィール"
                disabled={isLoading || isReordering}
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <Button
            color="secondary"
            size="md"
            onClick={onClose}
            disabled={isLoading || isReordering}
          >
            キャンセル
          </Button>
          <Button
            color="primary"
            size="md"
            onClick={handleSubmit}
            disabled={isLoading || isReordering || !categoryName.trim()}
          >
            {isLoading ? '更新中...' : '決定'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
